import React from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import FormControl from '@mui/material/FormControl'
import Grid from '@mui/material/Grid'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Select from '@mui/material/Select'
import Typography from '@mui/material/Typography'
import { router } from '@inertiajs/react'
import { SidebarLayout } from '@/components/layouts/SidebarLayout'

interface ApprovalData {
  id: number
  approvable_type: string
  approvable_type_label: string
  approvable_id: number
  status: 'pending' | 'approved' | 'denied' | 'revoked'
  status_color: string
  resource_description: string
  requested_by: { id: number; username: string; slug: string } | null
  decided_by: { id: number; username: string } | null
  decided_at: string | null
  reason: string | null
  decision_note: string | null
  created_at: string
}

interface AgentOption {
  id: number
  username: string
}

interface Props {
  approvals: ApprovalData[]
  filters: {
    status: string
    type: string
    agent: string
  }
  pending_count: number
  agents: AgentOption[]
}

const statusColors: Record<string, 'warning' | 'success' | 'error' | 'default'> = {
  pending: 'warning',
  approved: 'success',
  denied: 'error',
  revoked: 'default',
}

const typeOptions = [
  { value: 'all', label: 'All Types' },
  { value: 'secret_grant', label: 'Secret Access' },
  { value: 'connection', label: 'Connection' },
  { value: 'runner', label: 'Runner' },
]

const statusOptions = [
  { value: 'all', label: 'All Statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'denied', label: 'Denied' },
  { value: 'revoked', label: 'Revoked' },
]

export default function ApprovalsIndex({ approvals, filters, pending_count, agents }: Props) {
  function handleFilterChange(key: string, value: string) {
    router.get('/manage/approvals', { ...filters, [key]: value }, { preserveState: true })
  }

  function handleApprove(id: number) {
    router.post(`/manage/approvals/${id}/approve`)
  }

  function handleDeny(id: number) {
    router.post(`/manage/approvals/${id}/deny`)
  }

  function handleRevoke(id: number) {
    if (confirm('Are you sure you want to revoke this approval? Access will be removed immediately.')) {
      router.post(`/manage/approvals/${id}/revoke`)
    }
  }

  return (
    <SidebarLayout section="manage" title="Approvals">
      <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={filters.status}
            label="Status"
            onChange={(e) => handleFilterChange('status', e.target.value)}
          >
            {statusOptions.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Type</InputLabel>
          <Select
            value={filters.type}
            label="Type"
            onChange={(e) => handleFilterChange('type', e.target.value)}
          >
            {typeOptions.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Agent</InputLabel>
          <Select
            value={filters.agent}
            label="Agent"
            onChange={(e) => handleFilterChange('agent', e.target.value)}
          >
            <MenuItem value="all">All Agents</MenuItem>
            {agents.map((agent) => (
              <MenuItem key={agent.id} value={String(agent.id)}>{agent.username}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {pending_count > 0 && (
          <Chip
            label={`${pending_count} pending`}
            color="warning"
            size="small"
            variant="outlined"
          />
        )}
      </Box>

      {approvals.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 8 }}>
          No approvals found.
        </Typography>
      ) : (
        <Grid container spacing={2}>
          {approvals.map((approval) => (
            <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={approval.id}>
              <Paper
                elevation={0}
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  p: 2,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                  <Chip
                    label={approval.approvable_type_label}
                    size="small"
                    variant="outlined"
                  />
                  <Chip
                    label={approval.status}
                    size="small"
                    color={statusColors[approval.status] || 'default'}
                  />
                </Box>

                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>
                  {approval.resource_description}
                </Typography>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  By {approval.requested_by?.username || '—'}
                </Typography>

                {approval.reason && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={approval.reason}>
                    {approval.reason}
                  </Typography>
                )}

                {approval.decided_by && (
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>
                    Decided by {approval.decided_by.username}
                  </Typography>
                )}

                <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5 }}>
                  {new Date(approval.created_at).toLocaleDateString()}
                </Typography>

                <Box sx={{ display: 'flex', gap: 1, mt: 'auto' }}>
                  {approval.status === 'pending' && (
                    <>
                      <Button size="small" color="success" onClick={() => handleApprove(approval.id)}>
                        Approve
                      </Button>
                      <Button size="small" color="error" onClick={() => handleDeny(approval.id)}>
                        Deny
                      </Button>
                    </>
                  )}
                  {approval.status === 'approved' && (
                    <Button size="small" color="error" onClick={() => handleRevoke(approval.id)}>
                      Revoke
                    </Button>
                  )}
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}
    </SidebarLayout>
  )
}
